import { ReactElement } from 'react';
import * as React from 'react';
import UploadFile from './UploadZone';
import {
    Box,
    IconButton,
    Stack,
    Typography,
    styled,
    useTheme,
} from '@mui/material';
import Tooltip, { TooltipProps, tooltipClasses } from '@mui/material/Tooltip';
import { Formik, FormikProps } from 'formik';
import {
    AppFormikTextField,
    AppFormikTextSelect,
    ButtonComp,
} from 'shared/components/AppComponents';
import TypographyLevel from 'shared/components/Typography';
import DialogFormUpload from './DialogForm';
import DialogFileUpload from 'modules/home/upload-documents/upload-packet/DialogFile';
import ProgressZone from './ProgressZone';
import { useUploadPageState } from 'shared/hooks/UploadPage';
import {
    UploadPageInformationTooltip,
    UploadPageNextButton,
    UploadZoneDropDown,
    uploadPageboxChildStyle,
    uploadPageboxParentStyle,
    uploadPageinputNumberStyle,
} from 'assets/styles/modules/upload-documents/upload-packet/UploadPage';
import DialogComplete from './DialogComplete';
import { fileValidation } from 'shared/helpers/FileValidation';
import { SignedUrlTester, UploadSignFormat } from 'shared/hooks/APITester';
import { AzureStorage } from 'shared/helpers/AzureStorage';
import { ClientService } from 'services';
// import { ClientService, UploadService } from 'services';
// import { Toast } from 'shared/helpers';
import { formattingAction } from 'shared/helpers/FormatAction';
import {
    UploadFormScheme,
    UploadZoneScheme,
} from 'validators/UploadZoneScheme';
import InfoIcon from '@mui/icons-material/Info';
import { useFormikValidationEffect } from 'shared/helpers/Formik';
import { AxiosError } from 'axios';
import { Toast } from 'shared/helpers';

const SmallTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
))({
    [`& .${tooltipClasses.tooltip}`]: {
        maxWidth: 210,
    },
});

/**
 * Represents the UploadPage component.
 *
 * @returns {ReactElement} - The rendered component.
 */
const UploadPage = ({ setActiveState }: UploadPage): ReactElement => {
    const formikRef = React.useRef<FormikProps<FormikPropsValues> | null>(null);
    const theme = useTheme();
    /**
     * Ref for the file input element.
     * @type {React.RefObject<HTMLInputElement>}
     */
    const fileRef = React.useRef<HTMLInputElement>(null);

    /**
     * Ref for the boolean dialog status.
     * @type {React.RefObject<any>}
     */
    const dialogStatus = React.useRef<any>();
    const [clientValue, setClientValue] = React.useState<any>('');
    const [isFetchError, setFetchError] = React.useState<boolean>(false);

    /**
     * State and state setter for the upload page.
     * @type {[UploadPageState, React.Dispatch<React.SetStateAction<UploadPageState>>]}
     */
    const [state, setState] = useUploadPageState();

    const handleOpenDialog = React.useCallback(async () => {
        const validate = await formikRef.current
            ?.validateForm()
            .then((response) => {
                if (
                    response &&
                    (Object.keys(response).includes('clientName') ||
                        Object.keys(response).includes('binNumber'))
                ) {
                    formikRef.current?.setFieldTouched('clientName', true);
                    formikRef.current?.setFieldTouched('binNumber', true);
                    return false;
                }

                return true;
            })
            .catch(() => false);

        if (dialogStatus.current && validate) {
            dialogStatus.current.setOpenDialog(true);
        }
    }, []);
    const [isToggleOpened, setToggleOpened] = React.useState<boolean>(false);
    const [isValidInformation, setValidInformation] =
        React.useState<boolean>(true);
    const [isValidTextual, setValidTextual] = React.useState<boolean>(false);
    const [isReinitialize, setValueForReinitialization] =
        React.useState<boolean>(false);

    useFormikValidationEffect(
        (val) => {
            const { current: formik } = formikRef;
            if (formik) {
                const { isValidating } = formik;
                if (isValidating) {
                    setValidTextual(true);
                } else {
                    setValidTextual(val);
                }
                if (state.uploadProgress) {
                    setValueForReinitialization(true);
                    setState((prevState) => ({
                        ...prevState,
                        fileFailure: [],
                        failedBlob: false,
                    }));
                }
            }
        },
        'binNumber',
        formikRef
    );
    const [isReInitializeFromBinNumber, setReinitializeBinNumber] =
        React.useState<boolean>(false);
    React.useLayoutEffect(() => {
        if (state.uploadProgress) {
            setReinitializeBinNumber(true);
            setValueForReinitialization(false);
        }
    }, [isReinitialize]);

    React.useLayoutEffect(() => {
        if (state.uploadProgress === false) {
            setReinitializeBinNumber(false);
        }
    }, [state.uploadProgress]);
    /**
     * Handles the click event on the upload button.
     */
    const handleClick = (): void => {
        fileRef.current?.click();
        setState({ ...state, isChoosingFile: true });
    };

    /**
     * Handles the confirmation of the upload.
     */
    const confirmUpload = (): void => {
        setState({ ...state, openDialogFile: true, fileNetworkFailure: [] });
        setFetchError(false);
    };

    /**
     * Handles the file upload event.
     *
     * @param {ChangeEvent<HTMLInputElement>} event - The file upload event.
     */
    const handleFileUpload = React.useCallback(
        async (event: React.ChangeEvent<HTMLInputElement>): Promise<any> => {
            const { files } = event.target;
            if (formikRef.current) {
                const { getFieldHelpers, values } = formikRef.current;
                const fieldHelper = getFieldHelpers('attachment');
                if (files && files.length > 0) {
                    setState((prevState) => ({
                        ...prevState,

                        isProcessingData: true,
                    }));
                    const fileArray = Array.from<File>({
                        length: files.length,
                    }).map((i, index) => {
                        const fileName = files.item(index);
                        return fileName;
                    });
                    let blobData:
                        | {
                              blobdata: Uint8Array;
                              name: string;
                          }[]
                        | null = null;
                    const validation = await fileValidation(
                        event,
                        (data) => {
                            blobData = data;
                            setState((prevState) => ({
                                ...prevState,
                                isProcessingData: false,
                            }));
                        },
                        2 * 1024 * 1024 * 1024,
                        200,
                        'input'
                    )
                        .then((bool) => bool)
                        .catch(() => false);
                    if (validation && blobData) {
                        const filesArray = Array.from<any>({
                            length: files.length,
                        }).map((i: any, index: number) => {
                            const fileName = files.item(index)?.name;
                            return {
                                name: fileName ?? '',
                            };
                        });
                        fieldHelper.setValue(
                            values.attachment.concat(filesArray)
                        );
                        setState((prevState) => ({
                            ...prevState,
                            fileBlob:
                                prevState.fileBlob && blobData
                                    ? [...prevState.fileBlob, ...blobData]
                                    : blobData,
                            fileData: [...prevState.fileData, ...fileArray],
                            validateFile: true,
                            isChoosingFile: false,
                            isProcessingData: false,
                        }));
                    }
                } else {
                    setState((prevState) => ({
                        ...prevState,
                        isChoosingFile: false,
                        isProcessingData: false,
                    }));
                }
            }
        },
        [setState]
    );

    /**
     * Handles the removal of a file from the file data.
     *
     * @param {number} index - The index of the file to remove.
     */
    const handleRemoveFile = (index: number): void => {
        if (formikRef.current) {
            const { values, getFieldHelpers } = formikRef.current;
            const fieldHelper = getFieldHelpers('attachment');
            const updatedAttachmentData = [
                ...values.attachment.slice(0, index),
                ...values.attachment.slice(index + 1),
            ];
            fieldHelper.setValue(updatedAttachmentData);
            setState((prevState) => {
                const updatedFileData = [
                    ...prevState.fileData.slice(0, index),
                    ...prevState.fileData.slice(index + 1),
                ];
                const updatedFileBlob = [
                    ...prevState.fileBlob.slice(0, index),
                    ...prevState.fileBlob.slice(index + 1),
                ];
                if (updatedFileData.length < 1) {
                    return {
                        ...prevState,
                        validateFile: false,
                        fileData: updatedFileData,
                        fileBlob: updatedFileBlob,
                    };
                }
                return {
                    ...prevState,
                    fileData: updatedFileData,
                    fileBlob: updatedFileBlob,
                };
            });
        }
    };

    /**
     * Reference to store the total result count from the API response.
     */
    const totalResultRef = React.useRef<number>(0);

    /**
     * Fetches client data from the server using the specified offset.
     *
     * @param {number} offsetNumber - The offset value for pagination.
     * @returns {void}
     */
    const fetchClient = (offsetNumber: number): void => {
        ClientService.getEnabledClient({
            search: '',
            searchBy: '',
            offset: offsetNumber,
            limit: 100,
        }).then((response: { data: { msg: string; data: any[] } }) => {
            const result = response.data.data[0].paginatedResult;
            const totalResult = response.data.data[0].totalCount[0]?.count ?? 0;
            totalResultRef.current = totalResult;
            setState((prevState) => ({
                ...prevState,
                dataOfClient: [...prevState.dataOfClient, ...result],
            }));
        });
    };

    /**
     * Reference for scroll event.
     */
    const scrollContainerRef = React.useRef<HTMLDivElement | null>(null);
    const offsetRef = React.useRef<number>(1);
    const initialCallRef = React.useRef<boolean>(false);
    let isFetching = false;

    /**
     * Handle the scroll event.
     *
     * @returns {Promise<void>} A promise that resolves when the scroll event is handled.
     */
    const handleScroll = async (): Promise<void> => {
        if (
            scrollContainerRef.current &&
            scrollContainerRef.current.scrollTop +
                scrollContainerRef.current.clientHeight >=
                scrollContainerRef.current.scrollHeight - 10
        ) {
            scrollContainerRef.current.removeEventListener(
                'scroll',
                handleScroll
            );

            setState((prevState) => {
                if (prevState.dataOfClient.length >= totalResultRef.current) {
                    return prevState;
                }

                if (isFetching) {
                    return prevState;
                }

                isFetching = true;

                const offset = offsetRef.current;
                fetchClient(offset);

                setTimeout(() => {
                    isFetching = false;
                }, 1000);

                offsetRef.current = offset + 1;
                return prevState;
            });

            scrollContainerRef.current.addEventListener('scroll', handleScroll);
        }
    };

    /**
     * Initializes the component and fetches initial data on mount.
     *
     * @returns {void}
     */
    React.useEffect(() => {
        /**
         * Fetches the initial data from the server on mount.
         *
         * @returns {Promise<void>} A promise that resolves when the initial data is fetched.
         */
        const initialFetchData = async (): Promise<void> => {
            if (!initialCallRef.current) {
                const offset = offsetRef.current;
                await fetchClient(offset);
                offsetRef.current = offset + 1;
                initialCallRef.current = true;
            }
        };
        initialFetchData();
        if (state.dropdownOpen) {
            scrollContainerRef.current?.addEventListener(
                'scroll',
                handleScroll
            );
        }

        // Clean up event listener on unmount
        return () => {
            scrollContainerRef.current?.removeEventListener(
                'scroll',
                handleScroll
            );
        };
    }, [state.dropdownOpen]);

    React.useLayoutEffect(() => {
        if (!isValidInformation) {
            setToggleOpened(true);
        } else {
            setToggleOpened(false);
        }
    }, [isValidInformation]);

    /**
     * Sends the uploaded files as a packet.
     *
     * @param {any[]} files - Array of files to be uploaded.
     * @returns {void}
     */
    // const sendUpload = (files: any[]): void => {
    //     /**
    //      * Creates an array of files in a specific format.
    //      *
    //      * @type {any[]}
    //      */
    //     const filesBlob = files
    //         .filter((item: any) => item.progress === 100)
    //         .map((item: any) => ({
    //             packetId: item.packetId,
    //             status: 'successful',
    //         }));

    //     /**
    //      * Creates an new array
    //      *
    //      * @type {any[]}
    //      */
    //     const filesUpload = {
    //         clientId: clientValue.actions.uuid,
    //         uploadId: files[0].uploadId,
    //         uploads: filesBlob,
    //     };

    //     const reqPacket = async (): Promise<void> => {
    //         try {
    //             const response = await UploadService.uploadPacket(filesUpload);
    //             if (response) {
    //                 setState({
    //                     ...state,
    //                     openDialogComplete: true,
    //                     fileStatus: '-',
    //                 });
    //             }
    //         } catch (error: any) {
    //             Toast.showErrorMessage(error.response.data.msg);
    //             setState((prevState) => ({
    //                 ...prevState,
    //                 fileStatus: '-',
    //                 uploadProgress: false,
    //             }));
    //         }
    //     };

    //     // additional for encountering network latency
    //     setTimeout(async () => {
    //         await reqPacket();
    //     }, 1000);
    // };

    /**
     * Upload Progress confirmation
     *
     * @param {any} e - Dialog file status
     * @param {any} f - Upload progress status
     * @returns {Promise<void>}
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const confirmation = async (e: any, f: any): Promise<void> => {
        setState({
            ...state,
            openDialogFile: e,
            uploadProgress: f,
        });

        try {
            /**
             * Get name of array list files
             */
            const nameOfFiles = state.fileBlob.map(
                (i: { name: any }) => i.name
            );
            /**
             * Processes items in batch using SignedUrlTester and updates their properties.
             *
             * @param {string[]} names - Array of item names.
             * @returns {Promise<any[]>} Promise that resolves to an array of processed items.
             */
            const processItemsInBatch = async (
                names: string[]
            ): Promise<any[]> => {
                try {
                    /**
                     * @typedef {Object} SignedUrlTesterResponse
                     * @property {Array} uploadList - List of upload objects.
                     * @property {string} uploadId - Upload ID.
                     */

                    /**
                     * @type {SignedUrlTesterResponse}
                     */
                    const data: { uploadList: UploadSignFormat[] } =
                        await SignedUrlTester(
                            names,
                            clientValue.actions.uuid,
                            String(state.valueBin).toUpperCase()
                        );

                    const processedItems = state.fileBlob.map(
                        (item: any, index: number) => {
                            console.log('fileBlob reached');

                            if (
                                data.uploadList.some(
                                    (o: any) => o.file === item.name
                                )
                            ) {
                                console.log('name condition satisfied');

                                const i = data.uploadList.findIndex(
                                    (x: any) => x.file === item.name
                                );
                                console.log('outside file found');

                                if (i > -1) {
                                    console.log('inside file found');
                                    return {
                                        ...item,
                                        url: data.uploadList[i].url,
                                        fileName: data.uploadList[i].fileName,
                                        progress: 0,
                                        packetId: data.uploadList[i].packetId,
                                        uploadId: data.uploadList[i].uploadId,
                                        clientId: data.uploadList[i].clientId,
                                    };
                                }
                            }

                            return {
                                ...item,
                                url: data.uploadList[index].url,
                                fileName: data.uploadList[index].fileName,
                                progress: -1,
                                packetId: data.uploadList[index].packetId,
                                uploadId: data.uploadList[index].uploadId,
                                clientId: data.uploadList[index].clientId,
                            };
                        }
                    );

                    return processedItems;
                } catch (error: any) {
                    if (
                        error !== null &&
                        error instanceof AxiosError &&
                        error.code === 'ERR_NETWORK'
                    ) {
                        Toast.showErrorMessage(
                            'No internet connection, Please try again!'
                        );
                    }
                    if (
                        (error &&
                            error.response &&
                            error.response.status === 400 &&
                            error.response.data &&
                            error.response.data.msg ===
                                'Bin number already exists.') === false
                    ) {
                        setFetchError(true);
                    }

                    const errorItems = state.fileBlob.map((item: any) => ({
                        ...item,
                        progress: -1,
                    }));
                    return errorItems;
                }
            };

            const processedItems = await processItemsInBatch(nameOfFiles);

            setState((prevState) => ({
                ...prevState,
                fileBlob: processedItems,
            }));
            console.log(processedItems.length, 'upload batch length');
            processedItems.map(async (item: any, i) => {
                console.log(item.progress, 'progress details');

                try {
                    if (item.progress !== -1) {
                        await AzureStorage(
                            item.blobdata,
                            item.url,
                            (progress) => {
                                console.log(progress, 'progress');
                                setState((prevState) => ({
                                    ...prevState,
                                    fileBlob: prevState.fileBlob.map(
                                        (blobItem: { url: any }) =>
                                            blobItem.url === item.url
                                                ? { ...blobItem, progress }
                                                : blobItem
                                    ),
                                }));
                            },
                            (ev) => {
                                console.log(ev, 'ev');
                                setState((prevState) => ({
                                    ...prevState,
                                    fileStatus: ev,
                                }));
                            }
                        ).catch((e) => {
                            throw new Error(e);
                        });
                    }
                } catch (error) {
                    setState((prevState) => ({
                        ...prevState,
                        fileFailure: prevState.fileFailure.concat([i]),
                        isReInitializeFromBinNumber: false,
                    }));
                    setReinitializeBinNumber(false);
                    console.log(error, 'Error inside run block');
                }
            });
        } catch (error) {
            console.log(error, 'Error inside confirmation');
        }
    };

    /**
     * Upload Complete confirmation
     *
     * @param {boolean} ev - Status of boolean
     * @returns {void}
     */
    const confirmationComplete = (ev: boolean): void => {
        if (ev) {
            setActiveState(false);
            return;
        }
        setState((prevState) => ({
            ...prevState,
            uploadProgress: false,
        }));
    };

    const handleFileFailure = React.useCallback(
        (callback: (index: number) => void) => {
            state.fileBlob.map(async (item: any, i: number) => {
                try {
                    if (item.progress === 0) {
                        await AzureStorage(
                            item.blobdata,
                            item.url,
                            (progress) => {
                                setState((prevState) => ({
                                    ...prevState,
                                    fileBlob: prevState.fileBlob.map(
                                        (blobItem: { url: any }) =>
                                            blobItem.url === item.url
                                                ? { ...blobItem, progress }
                                                : blobItem
                                    ),
                                }));
                            },
                            (ev) => {
                                console.log(ev, 'ev');
                                setState((prevState) => ({
                                    ...prevState,
                                    fileStatus: ev,
                                }));
                            }
                        );
                        callback(i);
                    }
                } catch (error) {
                    setState((prevState) => ({
                        ...prevState,
                        fileFailure: prevState.fileFailure.concat([i]),
                    }));
                    console.log(error, 'Error inside run block');
                }
            });
        },
        [setState, state.fileBlob]
    );
    const handleFileReupload = React.useCallback(() => {
        const { openDialogFile, uploadProgress } = state;
        setFetchError(false);
        confirmation(openDialogFile, uploadProgress);
    }, [confirmation, state]);

    return (
        <>
            <Formik
                innerRef={formikRef}
                validationSchema={
                    state.uploadZone ? UploadZoneScheme : UploadFormScheme
                }
                initialValues={{
                    binNumber: '',
                    clientName: '',
                    clientName_error: false,
                    clientName_notselectable: false,
                    attachment: [],
                }}
                enableReinitialize
                onSubmit={() => {}}
            >
                {() => (
                    <>
                        <input
                            name="attachment"
                            type="file"
                            hidden
                            ref={fileRef}
                            accept=".zip"
                            multiple
                            onInput={handleFileUpload}
                        />
                        <Box
                            sx={{
                                ...uploadPageboxParentStyle,
                                [theme.breakpoints.down('md')]: {
                                    flexDirection: 'column',
                                    justifyContent: 'flex-start',
                                    alignItems: 'flex-start',
                                },
                            }}
                        >
                            <Box sx={uploadPageboxChildStyle(state)}>
                                <TypographyLevel variant="four">
                                    Checklist
                                </TypographyLevel>
                                <AppFormikTextSelect
                                    name="clientName"
                                    onChange={(e: any) => {
                                        setClientValue(e);
                                    }}
                                    options={Array.from(state.dataOfClient).map(
                                        (o) => ({
                                            ...o,
                                            actions: formattingAction(o),
                                        })
                                    )}
                                    path="name"
                                    sx={UploadZoneDropDown}
                                    placeholder="Select checklist"
                                    innerRef={scrollContainerRef}
                                    isOpen={state.dropdownOpen}
                                    autoComplete="off"
                                    setIsOpen={(e: any) =>
                                        setState({ ...state, dropdownOpen: e })
                                    }
                                />
                            </Box>

                            <Stack direction="row">
                                <Box>
                                    <TypographyLevel variant="four">
                                        Reference Number
                                    </TypographyLevel>
                                    <AppFormikTextField
                                        name="binNumber"
                                        className={
                                            isValidTextual
                                                ? 'input-bin bin-valid'
                                                : 'input-bin bin-not-valid'
                                        }
                                        readOnly={Boolean(true)}
                                        disabledError={state.uploadZone}
                                        manualError={!state.uploadZone}
                                        placeholder="Type here"
                                        sx={uploadPageinputNumberStyle(state)}
                                        onError={() => {
                                            setValidInformation(false);
                                        }}
                                        onValid={() => {
                                            setValidInformation(true);
                                        }}
                                        onChange={(e: {
                                            target: { value: any };
                                        }) =>
                                            setState({
                                                ...state,
                                                valueBin: String(
                                                    e.target.value
                                                ).toUpperCase(),
                                            })
                                        }
                                    />
                                </Box>
                                {state.uploadZone && (
                                    <Box sx={UploadPageInformationTooltip}>
                                        <Tooltip
                                            onOpen={() => {
                                                setToggleOpened(true);
                                            }}
                                            onClose={() => {
                                                setToggleOpened(false);
                                            }}
                                            open={isToggleOpened}
                                            title={
                                                <>
                                                    <Typography color="inherit">
                                                        Expected Reference
                                                        number format:
                                                        REFQAS1200AZ
                                                    </Typography>
                                                    <br />
                                                    <Typography color="inherit">
                                                        Reference Number must be
                                                        alphanumeric character
                                                        with maximum of 12
                                                        characters limit.
                                                    </Typography>
                                                    <br />
                                                    {/* <Typography color="inherit">
                                                        Example: ABCERY100TH
                                                    </Typography> */}
                                                </>
                                            }
                                        >
                                            <IconButton
                                                className="validation-icon-button"
                                                aria-label="validation info"
                                            >
                                                <InfoIcon
                                                    className={
                                                        isValidInformation
                                                            ? 'normal'
                                                            : 'warning'
                                                    }
                                                    fontSize="small"
                                                />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                )}
                            </Stack>

                            {!state.uploadZone && (
                                <Stack direction="row">
                                    <Box sx={UploadPageNextButton}>
                                        <TypographyLevel variant="four">
                                            &nbsp;
                                        </TypographyLevel>
                                        <ButtonComp
                                            isLoading={state.openDialog}
                                            loadingSize={24}
                                            loadingText="Loading"
                                            width="160px"
                                            onClick={handleOpenDialog}
                                            type="secondary"
                                            testid="nextButton"
                                            aria-haspopup="dialog"
                                        >
                                            Next
                                        </ButtonComp>
                                    </Box>
                                    <Box sx={UploadPageInformationTooltip}>
                                        <SmallTooltip
                                            onOpen={() => {
                                                setToggleOpened(true);
                                            }}
                                            onClose={() => {
                                                setToggleOpened(false);
                                            }}
                                            open={isToggleOpened}
                                            title={
                                                <>
                                                    <Typography color="inherit">
                                                        Expected Reference
                                                        number format:
                                                        REFQAS1200AZ,
                                                    </Typography>
                                                    <br />
                                                    <Typography color="inherit">
                                                        Reference Number must be
                                                        alphanumeric character
                                                        with maximum of 12
                                                        characters limit
                                                    </Typography>
                                                    <br />
                                                    {/* <Typography color="inherit">
                                                        Example: ABCERY100TH
                                                    </Typography> */}
                                                </>
                                            }
                                        >
                                            <IconButton
                                                className="validation-icon-button"
                                                aria-label="validation info"
                                            >
                                                <InfoIcon
                                                    className={
                                                        isValidInformation
                                                            ? 'normal'
                                                            : 'warning'
                                                    }
                                                    fontSize="small"
                                                />
                                            </IconButton>
                                        </SmallTooltip>
                                    </Box>
                                </Stack>
                            )}
                        </Box>

                        {state.uploadZone && !state.uploadProgress && (
                            <Box mt={3}>
                                <UploadFile
                                    handleClick={handleClick}
                                    state={state}
                                    confirmUpload={confirmUpload}
                                    setFileData={(e: number) =>
                                        handleRemoveFile(e)
                                    }
                                    fileRef={fileRef}
                                    callback={(data: any) => {
                                        setState((prevState) => ({
                                            ...prevState,
                                            fileBlob: data,
                                            fileNetworkFailure: [],
                                        }));
                                    }}
                                    updating={(data: any) => {
                                        if (formikRef.current) {
                                            const { getFieldHelpers } =
                                                formikRef.current;
                                            const fieldHelper =
                                                getFieldHelpers('attachment');

                                            fieldHelper.setValue(
                                                Array.from(data).map(
                                                    (i: any) => ({
                                                        name: i.name,
                                                    })
                                                )
                                            );
                                            setState((prevState) => ({
                                                ...prevState,
                                                fileData: data,
                                                validateFile: true,
                                            }));
                                        }
                                    }}
                                />
                            </Box>
                        )}
                    </>
                )}
            </Formik>

            {state.uploadProgress && (
                <Box mt={3} display="flex" flexDirection="column">
                    <ProgressZone
                        isFetchError={isFetchError}
                        fileCompleted={() => {
                            // sendUpload(files);
                            setState({
                                ...state,
                                openDialogComplete: true,
                                fileStatus: '-',
                            });
                        }}
                        fileReUpload={handleFileReupload}
                        state={state}
                        setState={setState}
                        fileReinit={handleFileFailure}
                        isReinitialize={isReInitializeFromBinNumber}
                        // Uncomment this if you want to manually upload
                        // setUploadZone={(e: boolean) => setState({ ...state, uploadZone: e })}
                        // setUploadProgress={(e: boolean) => setState({ ...state, uploadProgress: e })}
                    />
                </Box>
            )}

            <DialogFormUpload
                state={state}
                valueClient={clientValue}
                setOpenDialog={(e: boolean) =>
                    setState({ ...state, openDialog: e })
                }
                confirmation={(e: boolean, f: boolean) =>
                    setState((prevState) => ({
                        ...prevState,
                        openDialog: e,
                        uploadZone: f,
                    }))
                }
                ref={dialogStatus}
            />

            <DialogFileUpload
                openDialogFile={state.openDialogFile}
                setOpenDialogFile={(e: boolean) =>
                    setState({ ...state, openDialogFile: e })
                }
                confirmation={(e: boolean, f: boolean) => confirmation(e, f)}
            />

            <DialogComplete
                openDialogComplete={state.openDialogComplete}
                setOpenDialogComplete={(e: boolean) =>
                    setState({ ...state, openDialogComplete: e })
                }
                confirmation={(ev: boolean) => confirmationComplete(ev)}
            />
        </>
    );
};
export default UploadPage;
